import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './UseCaseDetail.css';
import VoiceVisualizer from './VoiceVisualizer';
import { AgentStatus } from '../types';
import { geminiClient } from '../services/geminiService';
import AIChatbotDemo from './AIChatbotDemo';

const useCaseData = {
  'ai-receptionist': {
    title: 'AI Receptionist',
    icon: 'fas fa-phone-alt',
    description: 'Handle incoming calls, schedule appointments, and provide customer support 24/7.',
    color: '#5a67d8',
    features: [
      'Answer calls instantly',
      'Schedule appointments',
      'Provide business information',
      'Route calls to appropriate departments',
      'Handle after-hours inquiries',
      'Multi-language support'
    ]
  },
  '24-7-dispatcher': {
    title: '24/7 Dispatcher',
    icon: 'fas fa-truck',
    description: 'Automate dispatch operations, route calls, and manage logistics around the clock.',
    color: '#c44569',
    features: [
      'Automated call routing',
      'Real-time dispatch management',
      'Priority handling',
      'Status updates',
      'Multi-channel support',
      'Multi-language support'
    ]
  },
  'ai-callback': {
    title: 'AI Callback',
    icon: 'fas fa-phone-volume',
    description: 'Schedule callbacks, manage missed calls, and ensure no customer inquiry goes unanswered.',
    color: '#2c5aa0',
    features: [
      'Schedule callbacks',
      'Missed call management',
      'Priority queuing',
      'Automated reminders',
      'Callback tracking',
      'Multi-language support'
    ]
  },
  'ai-chatbot': {
    title: 'AI Chatbot',
    icon: 'fas fa-comments',
    description: 'Provide instant text-based support, answer FAQs, and guide customers through your website.',
    color: '#2d8659',
    features: [
      'Instant responses',
      'Website navigation',
      'FAQ handling',
      'Lead generation',
      'Multi-language support'
    ]
  },
  'customer-support': {
    title: 'Customer Support',
    icon: 'fas fa-headset',
    description: 'Handle customer inquiries, troubleshoot issues, and provide technical support instantly.',
    color: '#c7365f',
    features: [
      'Issue resolution',
      'Product information',
      'Troubleshooting guides',
      'Ticket creation',
      'Escalation management',
      'Multi-language support'
    ]
  },
  'lead-qualification': {
    title: 'Lead Qualification',
    icon: 'fas fa-filter',
    description: 'Qualify leads, gather information, and route high-value prospects to your sales team.',
    color: '#d97706',
    features: [
      'Lead scoring',
      'Information gathering',
      'Qualification questions',
      'CRM integration',
      'Sales team routing',
      'Multi-language support'
    ]
  },
  'appointment-scheduling': {
    title: 'Appointment Scheduling',
    icon: 'fas fa-calendar-check',
    description: 'Automate booking, rescheduling, and cancellation of appointments with calendar integration.',
    color: '#0d9488',
    features: [
      'Calendar integration',
      'Availability checking',
      'Appointment reminders',
      'Rescheduling',
      'Cancellation handling',
      'Multi-language support'
    ]
  },
  'order-tracking': {
    title: 'Order Tracking',
    icon: 'fas fa-box',
    description: 'Provide real-time order status updates, shipping information, and delivery notifications.',
    color: '#0891b2',
    features: [
      'Order status updates',
      'Shipping tracking',
      'Delivery notifications',
      'Order history',
      'Return processing',
      'Multi-language support'
    ]
  },
  'technical-support': {
    title: 'Technical Support',
    icon: 'fas fa-tools',
    description: 'Troubleshoot technical issues, provide step-by-step guidance, and escalate complex problems.',
    color: '#dc2626',
    features: [
      'Technical troubleshooting',
      'Step-by-step guidance',
      'Documentation access',
      'Issue escalation',
      'Follow-up scheduling',
      'Multi-language support'
    ]
  }
};

const FeaturesGrid = ({ features, useCaseColor }) => {
  const featureDetails = {
    // AI Receptionist
    'Answer calls instantly': [
      'Eliminates wait times and busy signals',
      'Never miss a potential customer',
      'Improves satisfaction and reduces abandonment'
    ],
    'Schedule appointments': [
      'Book, reschedule, or cancel with natural conversation',
      'Real-time calendar integration',
      'Automatic confirmation reminders'
    ],
    'Provide business information': [
      'Instant answers about services, hours, and location',
      'Handles pricing and policy inquiries',
      'No human intervention required'
    ],
    'Route calls to appropriate departments': [
      'Intelligent routing based on customer needs',
      'Natural language understanding',
      'Ensures callers reach qualified team members'
    ],
    'Handle after-hours inquiries': [
      'Available 24/7 for customer support',
      'Capture leads and schedule anytime',
      'No additional staffing costs'
    ],
    
    // 24/7 Dispatcher
    'Automated call routing': [
      'Routes calls based on type, urgency, and workload',
      'Reduces response times significantly',
      'Ensures optimal resource allocation'
    ],
    'Real-time dispatch management': [
      'Tracks assignments and status updates in real-time',
      'Provides instant visibility into operations',
      'Enables better decision-making and coordination'
    ],
    'Priority handling': [
      'Prioritizes based on urgency and customer type',
      'Handles critical requests first',
      'Maintains efficiency for routine operations'
    ],
    'Status updates': [
      'Real-time updates on requests and dispatches',
      'Estimated completion times for customers',
      'Reduces inquiry calls and improves satisfaction'
    ],
    'Multi-channel support': [
      'Handles phone, text, email, and web forms',
      'Centralizes all communication channels',
      'Streamlined operations and better customer experience'
    ],
    
    // AI Callback
    'Schedule callbacks': [
      'Customers choose their preferred callback time',
      'Reduces wait times and improves satisfaction',
      'Automatic queue management and timely follow-ups'
    ],
    'Missed call management': [
      'Automatically captures all missed calls',
      'Proactive outreach to customers',
      'Never lose a potential customer'
    ],
    'Priority queuing': [
      'Queues based on customer value and urgency',
      'Business rules determine priority',
      'High-priority customers get faster responses'
    ],
    'Automated reminders': [
      'Reduces no-shows with timely reminders',
      'Multiple reminder channels (SMS, email, phone)',
      'Improves callback success rates'
    ],
    'Callback tracking': [
      'Tracks all attempts and outcomes',
      'Analytics for optimization',
      'Insights into performance and satisfaction'
    ],
    
    // AI Chatbot
    'Instant responses': [
      '24/7 instant, accurate responses',
      'Eliminates wait times completely',
      'Improves satisfaction and engagement'
    ],
    'Website navigation': [
      'Guides customers to find what they need',
      'Reduces bounce rates',
      'Improves overall user experience'
    ],
    'FAQ handling': [
      'Automatically answers common questions',
      'Accurate, consistent information',
      'Reduces support workload'
    ],
    'Lead generation': [
      'Qualifies leads through intelligent conversation',
      'Gathers contact information and requirements',
      'Routes qualified leads to sales teams'
    ],
    'Multi-language support': [
      'Supports multiple languages',
      'Serves global customer base',
      'Breaks down language barriers'
    ],
    
    // Customer Support
    'Issue resolution': [
      'Guided troubleshooting and automated solutions',
      'Reduces support tickets',
      'Improves first-contact resolution rates'
    ],
    'Product information': [
      'Detailed specs and comparisons',
      'Helps customers make informed decisions',
      'Reduces sales cycle time'
    ],
    'Troubleshooting guides': [
      'Step-by-step guides for common problems',
      'Empowers customers to resolve issues independently',
      'Reduces support costs'
    ],
    'Ticket creation': [
      'Automatic ticket creation when needed',
      'Ensures nothing falls through the cracks',
      'Maintains accountability'
    ],
    'Escalation management': [
      'Intelligent escalation to appropriate tiers',
      'Customers get the right level of help',
      'Optimizes resource usage'
    ],
    
    // Lead Qualification
    'Lead scoring': [
      'Scores based on behavior and responses',
      'Helps prioritize follow-ups',
      'Focus on high-value prospects'
    ],
    'Information gathering': [
      'Systematic information collection',
      'Natural conversation approach',
      'Builds comprehensive lead profiles'
    ],
    'Qualification questions': [
      'Determines fit, budget, and timeline',
      'Identifies decision-making authority',
      'Ensures focus on qualified opportunities'
    ],
    'CRM integration': [
      'Automatic CRM record updates',
      'Triggers workflows seamlessly',
      'Eliminates manual data entry'
    ],
    'Sales team routing': [
      'Routes based on territory and expertise',
      'Considers availability',
      'Ensures quick handling by the right person'
    ],
    
    // Appointment Scheduling
    'Calendar integration': [
      'Real-time availability checking',
      'Prevents double-booking',
      'Synchronizes across all devices'
    ],
    'Availability checking': [
      'Checks multiple staff and locations',
      'Finds best appointment times',
      'Works for both you and customers'
    ],
    'Appointment reminders': [
      'SMS, email, or phone reminders',
      'Customizable timing and messaging',
      'Reduces no-shows'
    ],
    'Rescheduling': [
      'Easy rescheduling through conversation',
      'Automatic calendar updates',
      'Confirmation notifications sent'
    ],
    'Cancellation handling': [
      'Graceful cancellation processing',
      'Offers to reschedule automatically',
      'Maintains customer relationships'
    ],
    
    // Order Tracking
    'Real-time tracking': [
      'Real-time order status updates',
      'Complete tracking information',
      'Keeps customers informed throughout'
    ],
    'Delivery updates': [
      'Proactive delivery notifications',
      'Estimated arrival times',
      'Delivery confirmations'
    ],
    'Order inquiries': [
      'Handles status checks and modifications',
      'Issue resolution support',
      'Instant answers without calling'
    ],
    'Inventory checking': [
      'Real-time product availability',
      'Helps customers make informed decisions',
      'Reduces order cancellations'
    ],
    'Return processing': [
      'Automated return workflows',
      'Streamlined refund processes',
      'Maintains satisfaction and compliance'
    ],
    
    // Sales Assistant
    'Product recommendations': [
      'Personalized based on needs and preferences',
      'Increases average order value',
      'Improves customer satisfaction'
    ],
    'Price inquiries': [
      'Instant accurate pricing information',
      'Includes discounts and promotions',
      'Helps quick purchasing decisions'
    ],
    'Order processing': [
      'Natural conversation order processing',
      'Secure payment collection',
      'Reduces cart abandonment'
    ],
    'Upselling and cross-selling': [
      'Intelligent product suggestions',
      'Complementary products and upgrades',
      'Increases revenue while adding value'
    ],
    'Customer history': [
      'Accesses purchase history and preferences',
      'Provides personalized service',
      'Creates seamless, tailored experiences'
    ]
  };

  return (
    <div className="features-grid-container">
      <div className="features-grid">
        {features.map((feature, index) => (
          <div key={index} className="feature-grid-card">
            <div className="feature-card-header">
              <i className="fas fa-check-circle"></i>
              <h3>{feature}</h3>
            </div>
            <ul className="feature-card-description">
              {(featureDetails[feature] || [`${feature} capability designed to streamline your business operations and enhance customer satisfaction.`]).map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const UseCaseDetail = () => {
  const { useCaseId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(AgentStatus.DISCONNECTED);
  const [amplitude, setAmplitude] = useState(0);
  const [isChatbotOpen, setIsChatbotOpen] = useState(useCaseId === 'ai-chatbot');

  const useCase = useCaseData[useCaseId];

  useEffect(() => {
    if (!useCase) {
      navigate('/use-cases');
      return;
    }

    // Set up callbacks
    geminiClient.onStatusChange = (newStatus) => {
      setStatus(newStatus);
    };

    geminiClient.onAudioData = (amp) => {
      setAmplitude(amp);
    };

    geminiClient.onLogMessage = (source, text) => {
      console.log(`[${source}]: ${text}`);
    };

    geminiClient.onToolCall = (name, args) => {
      console.log(`Tool called: ${name}`, args);
    };

    // Cleanup on unmount
    return () => {
      geminiClient.disconnect();
    };
  }, [useCase, navigate]);

  const handleConnect = async () => {
    if (status === AgentStatus.DISCONNECTED) {
      // Pass the use case ID to connect so it uses the correct system instruction
      await geminiClient.connect(useCaseId);
    } else {
      // End call - disconnect regardless of current status
      console.log('Ending call, current status:', status);
      await geminiClient.disconnect();
      // Force status update to ensure UI updates
      setStatus(AgentStatus.DISCONNECTED);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'disconnected': 'Ready to Connect',
      'connecting': 'Connecting...',
      'listening': 'Listening',
      'speaking': 'AI is Speaking',
      'thinking': 'Processing...',
      'error': 'Connection Error'
    };
    return labels[status] || status;
  };

  if (!useCase) {
    return null;
  }

  return (
    <div className="use-case-detail-page">
      <div className="use-case-detail-hero" style={{ background: `linear-gradient(135deg, ${useCase.color} 0%, ${useCase.color}dd 100%)` }}>
        <div className="container">
          <button className="back-button" onClick={() => navigate('/use-cases')}>
            <i className="fas fa-arrow-left"></i>
            Back to Use Cases
          </button>
          <div className="use-case-header">
            <div className="use-case-header-icon">
              <i className={useCase.icon}></i>
            </div>
            <div className="use-case-header-text">
              <h1>{useCase.title}</h1>
              <p>{useCase.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="use-case-detail-content">
        <div className="container">
          <div className={`main-content-layout ${useCaseId === 'ai-chatbot' ? 'chatbot-layout' : ''}`}>
            {/* Left Side - Voice AI Demo or Chatbot */}
            {useCaseId === 'ai-chatbot' ? (
              <div className="chatbot-section">
                <div className="chatbot-card">
                  <div className="chatbot-demo-container">
                    <AIChatbotDemo 
                      isOpen={isChatbotOpen} 
                      onClose={() => setIsChatbotOpen(false)}
                      inline={true}
                      headerColor={useCase.color}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="voice-agent-section">
                <div className="demo-compact-card">
                  <div className="voice-demo-container">
                    <div className="voice-visualizer-section">
                      <div className="voice-visualizer-compact">
                        <div className="visualizer-wrapper">
                          <VoiceVisualizer 
                            amplitude={amplitude} 
                            isActive={status !== AgentStatus.DISCONNECTED} 
                            status={status} 
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="voice-controls-section">
                      <button
                        onClick={handleConnect}
                        className={`voice-play-button ${status === AgentStatus.DISCONNECTED ? 'play' : 'stop'}`}
                        disabled={status === 'connecting'}
                        style={{ background: status === AgentStatus.DISCONNECTED ? `linear-gradient(135deg, ${useCase.color} 0%, ${useCase.color}dd 100%)` : undefined }}
                      >
                        {status === AgentStatus.DISCONNECTED ? (
                          <>
                            <i className="fas fa-phone"></i>
                            <span>Start Call</span>
                          </>
                        ) : status === 'connecting' ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Connecting...</span>
                          </>
                        ) : (
                          <>
                            <i className="fas fa-phone-slash"></i>
                            <span>End Call</span>
                          </>
                        )}
                      </button>
                      
                      <div className="status-indicator">
                        <div className={`status-dot status-${status}`}></div>
                        <span className="status-text">{getStatusLabel(status)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Right Side - Features Grid */}
            <div className="features-section">
              <FeaturesGrid features={useCase.features} useCaseColor={useCase.color} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseCaseDetail;

